/* eslint-disable */
import axios from 'axios'
import * as R from 'ramda'
import { message } from 'antd'
import Session from '@/utils/session'
import BasicConfig from '@/config/basic'
import { objToFormData, objToQuerystring, objToEncryptedData } from '@/utils/helpers'
import { handleLoginExpired, refreshTokenByType } from './refreshToken'

/** Java 后端：`java` 与 `bodyFormatConfig.java` 一致（form-urlencoded）；`javaJson` 仅 body 为 JSON，响应/鉴权与 java 相同；`javaMultipart` 为 FormData 上传 */
const JAVA_API_FORMAT = {
  tokenHeader: 'Authorization',
  tokenSource: 'redux', // 从 Redux 获取 token
  tokenPath: 'javaToken', // Redux 路径（相对于 app）
  tokenPrefix: 'Bearer',
  statusCodeField: 'code',
  // 与常见 Spring 等后端一致：code=0 表示成功；部分接口仍用 200
  successCodes: [0, 200, '0', '200'],
  loginExpiredCodes: [401],
  dataField: 'data',
  messageField: 'msg'
}

const isJavaAxiosFamily = (t) => t === 'java' || t === 'javaJson' || t === 'javaMultipart'

// API 格式配置 - 支持不同系统的响应格式
// 导出供 refreshToken.js 使用
export const API_FORMAT_CONFIG = {
  // 默认系统格式
  default: {
    tokenHeader: 'Authorization',
    tokenSource: 'redux', // 从 Redux 获取 token
    tokenPath: 'javaToken', // Redux 路径（相对于 app）
    tokenPrefix: 'Bearer',
    statusCodeField: 'code',
    successCodes: [200],
    loginExpiredCodes: [401],
    dataField: 'data',
    messageField: 'msg'
  },
  // ERP 系统格式
  erpAPI: {
    tokenHeader: 'token',
    tokenPrefix: '',
    statusCodeField: 'errcode',
    successCodes: [1], // errcode=1 表示成功
    loginExpiredCode: 3, // 保留向后兼容
    loginExpiredCodes: [2, 3], // 2=请登录/登录信息无效（「没有权限」不算）；3=Token失效
    noPermissionCode: 80000, // 无权限：不取数、不踢登录
    dataField: 'object', // 如果没有 object 字段，使用整个 data
    messageField: 'errinfo'
  },
  erpIndex: {
    tokenHeader: 'token',
    tokenPrefix: '',
    statusCodeField: 'errcode',
    successCodes: [1], // errcode=1 表示成功
    loginExpiredCode: 3, // 保留向后兼容
    loginExpiredCodes: [3, -1, 80001], // 多个登录失效码：3=Token失效, -1=登出/失效, 80001=请重新登录
    noPermissionCode: 80000, // 无权限码，前端会隐藏相关图表
    dataField: 'object', // 如果没有 object 字段，使用整个 data
    messageField: 'errinfo'
  },
  // 萤石云 API 格式
  ys7: {
    tokenHeader: 'accessToken',
    tokenSource: 'redux', // 从 Redux 获取 token
    tokenPath: 'ys7Token', // Redux 路径（相对于 app）
    statusCodeField: 'code',
    successCodes: ['200', 200], // code=200 表示成功
    dataField: 'data',
    messageField: 'msg',
    // POST/PUT 请求：将 accessToken 放在 body 中
    postTokenInBody: true,
    // 保持原始响应格式，便于调用方处理
    keepRawResponse: true,
    // 不自动显示错误消息，由调用方处理
    noAutoError: true,
    // 萤石云 API 不使用登录失效码（使用 accessToken，不是 session token）
    loginExpiredCode: null,
    loginExpiredCodes: [] // 空数组，表示没有登录失效码
  },
  java: JAVA_API_FORMAT,
  /** 同 java，请求体为 JSON（见 bodyFormatConfig.javaJson） */
  javaJson: JAVA_API_FORMAT,
  /** 同 java，请求体为 multipart FormData（见 bodyFormatConfig.javaMultipart） */
  javaMultipart: JAVA_API_FORMAT,
  python: {
    tokenHeader: 'Authorization',
    tokenPrefix: 'Bearer ',
    statusCodeField: 'code',
    successCodes: [0, 1, 200],
    loginExpiredCode: 3,
    dataField: 'object',
    messageField: 'message'
  }
}

// 根据配置获取格式设置
// 完全基于配置决定，不进行 URL 判断
// 导出供 refreshToken.js 使用
export const getApiFormat = (config) => {
  // 统一使用 config.axiosType（请求拦截器已自动提取并保存）
  // 注意：axiosType 在请求拦截器中被提取到 config.axiosType，因为：
  // - POST/PUT 请求：config.data 会被转换成字符串，无法读取 data.axiosType
  // - GET 请求：params.axiosType 会被删除（避免发送到服务器）
  if (config?.axiosType && API_FORMAT_CONFIG[config.axiosType]) {
    return API_FORMAT_CONFIG[config.axiosType]
  }
  
  // 向后兼容：如果请求拦截器未处理（理论上不应该发生），尝试从原始位置读取
  // 检查 data 中的 axiosType（POST/PUT 请求，仅在 data 未被转换时可用）
  if (config?.data?.axiosType && typeof config.data === 'object' && API_FORMAT_CONFIG[config.data.axiosType]) {
    return API_FORMAT_CONFIG[config.data.axiosType]
  }
  
  // 检查 params 中的 axiosType（GET 请求，仅在未被删除时可用）
  if (config?.params?.axiosType && API_FORMAT_CONFIG[config.params.axiosType]) {
    return API_FORMAT_CONFIG[config.params.axiosType]
  }
  
  // 默认格式
  return API_FORMAT_CONFIG.default
}

// ==================== Token 处理函数（按 API 类型）====================

/**
 * 处理萤石云 API (ys7) 的 token
 * - GET 请求：token 放在 Header
 * - POST/PUT 请求：token 放在 body 中
 */
const handleYs7Token = (config, format) => {
  // 少数接口仅传 appKey/appSecret 等，不要求 body 中带账号 accessToken（如 lapp/trust/device/v2/token/get）
  if (config.skipYs7AccessTokenInject) return

  // 从 Redux 获取 token（Ys7 特殊：只从 Redux 获取，不使用通用方法）
  const token = getToken(format)
  if (!token) return
  
  const extractToHeaders = config.extractToHeaders || {}
  
  if (config.method === 'get') {
    // GET 请求：token 放在 Header
    config.headers[format.tokenHeader] = token
    
    // 处理 extractToHeaders
    if (Object.keys(extractToHeaders).length > 0) {
      config.headers = { ...config.headers, ...extractToHeaders }
      if (config.params && typeof config.params === 'object') {
        Object.keys(extractToHeaders).forEach(field => {
          delete config.params[field]
        })
      }
    }
  } else {
    // POST/PUT 请求：token 放在 body 中
    const tokenFieldName = format.tokenHeader
    
    if (Object.keys(extractToHeaders).length > 0) {
      // 有 extractToHeaders：token 和字段都放在 Header
      config.headers[format.tokenHeader] = token
      config.headers = { ...config.headers, ...extractToHeaders }
      
      // 从 body 中移除已添加到 Header 的字段
      if (config.data && typeof config.data === 'object') {
        Object.keys(extractToHeaders).forEach(field => {
          delete config.data[field]
        })
      } else if (config.data && typeof config.data === 'string') {
        try {
          const params = new URLSearchParams(config.data)
          Object.keys(extractToHeaders).forEach(field => {
            params.delete(field)
          })
          config.data = params.toString()
        } catch (e) {
          // 解析失败，保持原样
        }
      }
    } else {
      // 没有 extractToHeaders：token 放在 body 中
      if (config.data && typeof config.data === 'string') {
        try {
          const params = new URLSearchParams(config.data)
          if (!params.get(tokenFieldName)) {
            params.set(tokenFieldName, token)
            config.data = params.toString()
          }
        } catch (e) {
          // 解析失败，保持原样
        }
      } else if (config.data && typeof config.data === 'object') {
        if (!config.data[tokenFieldName]) {
          config.data[tokenFieldName] = token
        }
      }
    }
  }
}

/**
 * 处理 Java API 的 token
 * - 所有请求：token 放在 Header
 */
const handleJavaToken = (config, format) => {
  const token = getToken(format)
  if (!token) return
  
  const tokenValue = formatToken(token, format)
  setTokenToHeader(config, tokenValue, format)
}

/**
 * 处理 ERP API (erpAPI/erpIndex) 的 token
 * - 使用 Session token
 * - 注意：Content-Type 在 bodyFormatConfig 中统一设置
 */
const handleErpToken = (config, format) => {
  const token = getToken(format)
  if (!token) return
  
  const tokenValue = formatToken(token, format)
  setTokenToHeader(config, tokenValue, format)
}

/**
 * 处理默认 API 的 token
 * - 使用 Session token
 * - 注意：Content-Type 在 bodyFormatConfig 中统一设置
 */
const handleDefaultToken = (config, format) => {
  const token = getToken(format)
  if (!token) return
  
  const tokenValue = formatToken(token, format)
  setTokenToHeader(config, tokenValue, format)
}

/**
 * 从 Redux 获取 token
 */
const getReduxToken = (tokenPath) => {
  try {
    if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
      const state = window.__REDUX_STORE__.getState()
      return state?.app?.[tokenPath] || ''
    }
  } catch (e) {
    console.warn('获取 Redux token 失败:', e)
  }
  return ''
}

const getToken = (format) => {
  if (format.tokenSource === 'redux') {
    // 从 Redux 获取
    return getReduxToken(format.tokenPath)
  } else {
    // 从 Session 获取（默认）
    return Session.getToken() || ''
  }
}

/**
 * 检查是否有 token，如果没有则拒绝请求
 * @param {Object} config - axios 配置
 * @param {Object} format - API 格式配置
 * @returns {boolean} - 如果有 token 返回 true，否则返回 false
 */
const checkTokenBeforeRequest = (config, format) => {
  const token = getToken(format)
  
  // 如果配置了 __skipTokenCheck，跳过 token 检查（用于登录接口等）
  if (config?.__skipTokenCheck) {
    return true
  }
  
  // 如果没有 token，拒绝请求
  if (!token || token.trim() === '') {

    // 返回一个被拒绝的 Promise
    return false
  }
  
  return true
}

/**
 * 格式化 token 的通用方法
 * 根据 format.tokenPrefix 添加前缀
 */
const formatToken = (token, format) => {
  if (!token) return ''
  
  const prefix = format.tokenPrefix || ''
  if (!prefix) return token
  
  // 如果 prefix 已经包含空格，直接拼接；否则添加空格
  return prefix.endsWith(' ') ? prefix + token : prefix + ' ' + token
}

/**
 * 设置 token 到 Header 的通用方法
 * 
 * @param {Object} config - axios 配置
 * @param {string} tokenValue - 格式化后的 token 值
 * @param {Object} format - API 格式配置
 */
const setTokenToHeader = (config, tokenValue, format) => {
  if (!tokenValue) return
  config.headers[format.tokenHeader] = tokenValue
}

// 声明一个数组用于存储每个请求的取消函数和axios标识
let pending = []
let cancelToken = axios.CancelToken
let removePending = (config) => {
  for (let p in pending) {
    // 当当前请求在数组中存在时执行函数体
    if (pending[p].u === config.url + '&' + config.method) {
      // 执行取消操作
      pending[p].f()
      pending.splice(p, 1)
    }
  }
}

// 单接口无权限：不取数、不踢登录（ERP errcode=2 会与「请登录」复用，必须看文案）
const isNoPermissionResponse = (statusCode, format, data) => {
  if (format.noPermissionCode && statusCode === format.noPermissionCode) return true
  const info = data && typeof data === 'object' ? data[format.messageField] : ''
  return typeof info === 'string' && info.includes('没有权限')
}

// 检查是否为登录失效码（支持单个或数组）
const isLoginExpired = (statusCode, format, data) => {
  if (isNoPermissionResponse(statusCode, format, data)) return false
  // 优先使用 loginExpiredCodes 数组
  if (format.loginExpiredCodes && Array.isArray(format.loginExpiredCodes)) {
    return format.loginExpiredCodes.includes(statusCode)
  }
  // 向后兼容：使用单个 loginExpiredCode
  return statusCode === format.loginExpiredCode
}

const formatResponse = response => {
  const { headers, data, config } = response
  // 获取 API 格式配置
  const format = getApiFormat(config)
  
  if (/x-ms-excel/.test(headers['content-type'])) {
    const contentDisposition = headers['content-disposition'] || ''
    const [, fileName] = contentDisposition.split('=')
    return {
      fileName: fileName ? decodeURIComponent(fileName) : fileName,
      data
    }
  }
  
  // 根据配置获取状态码
  const statusCode = data[format.statusCodeField]

  // 单接口无权限：不落库、不 toast、不踢登录
  if (isNoPermissionResponse(statusCode, format, data)) {
    return Promise.reject(data)
  }

  // 检查是否需要登录（支持多个登录失效码）
  // 注意：token 刷新/获取接口会返回 401 时，不应在这里触发跳转或刷新死循环
  if (!config?.__skipAuthRefresh && isLoginExpired(statusCode, format, data)) {
    // 清除 token 和 cookies
    // - 只有“session token 体系”（默认/ERP）才需要清理 ik cookie
    // - Java token 存在 Redux/localStorage，不写入 cookies，不能用 cookies 来校验/清理
    // 从 config.axiosType 获取（请求拦截器已自动提取）
    const axiosType = config?.axiosType || 'default'
    
    // 尝试统一处理登录失效（自动刷新 token）
    // 注意：响应拦截器中不直接刷新，因为错误拦截器中已经处理了重试逻辑
    // 这里只处理不支持自动刷新的类型（如 ERP），直接跳转登录
    const shouldTryRefresh = isJavaAxiosFamily(axiosType) || axiosType === 'ys7'
    
    if (!shouldTryRefresh) {
      // 不支持自动刷新的类型（如 ERP），直接跳转登录
      const isSessionTokenSource = !format?.tokenSource || format.tokenSource === 'session'
      if (isSessionTokenSource) {
        Session.removeIkCookie()
      }
      // 使用 window.location 强制跳转到登录页
      window.location.replace('/system/login')
      return Promise.reject(data)
    }
    // 支持自动刷新的类型（java/ys7），在错误拦截器中处理，这里不处理
  }

  // 检查是否成功
  if (format.successCodes.includes(statusCode)) {
    // 根据配置决定返回格式
    // 如果配置了 keepRawResponse，保持原始响应格式（如萤石云 API）
    if (format.keepRawResponse) {
      return {
        data: data
      }
    }
    
    // 其他 API：使用格式化后的响应
    const meta = { ...R.dissoc(format.dataField, data), headers }
    // 根据配置获取数据字段，如果没有则使用整个 data
    const responseData = data[format.dataField] !== undefined ? data[format.dataField] : data
    return {
      data: responseData,
      meta
    }
  }
  
  // 显示错误消息
  const errorMessage = data[format.messageField] || 'error'
  // 如果消息中包含"成功"字样，不显示错误提示
  // 如果配置了 noAutoError，不自动显示错误（如萤石云 API）
  if (!format.noAutoError && errorMessage && !errorMessage.includes('成功')) {
    message.error(errorMessage)
  }
  return Promise.reject(data)
}

// default axios config
/* eslint-disable no-undef */
if (process.env.NODE_ENV === 'development') {
  // 开发环境：设置为空字符串，因为服务文件中的路径已经包含了 /api 前缀
  // 例如：dashboard.js 中的路径是 `${BasicConfig.szMysqlDomain}/api/dashboard/...`
  // 当 szMysqlDomain 为空字符串时，路径变成 `/api/dashboard/...`
  // 如果这里再设置 baseURL = '/api'，会导致路径变成 `/api/api/dashboard/...`
  axios.defaults.baseURL = ''
} else {
  axios.defaults.baseURL = BasicConfig.domain
}

// 注意：跨域请求如果携带 Cache-Control/Pragma，可能触发 preflight，
// 且后端未放行这些 header 时会导致 CORS 报错。
// 因此不要在全局 defaults 里强制给所有 GET 加 no-cache（需要时在同源请求场景单独加）。
// axios.defaults.headers.get['Cache-Control'] = 'no-cache'
// axios.defaults.headers.get['Pragma'] = 'no-cache'
// axios.defaults.withCredentials = true

/**
 * 判断是否跨域请求（仅浏览器环境）
 * - 绝对 URL 才可能跨域；相对 URL 一般属于同源（或走本地 proxy）
 */
const isCrossOriginRequest = (finalUrl) => {
  try {
    if (typeof window === 'undefined' || !finalUrl) return false
    if (!/^https?:\/\//i.test(finalUrl)) return false
    const reqUrl = new URL(finalUrl, window.location.origin)
    return reqUrl.origin !== window.location.origin
  } catch (e) {
    return false
  }
}

/**
 * 删除请求头（兼容 axios headers 的多层结构：headers/common/headers[method]）
 */
const deleteHeader = (config, headerName) => {
  if (!config || !config.headers) return
  const method = (config.method || 'get').toLowerCase()
  const candidates = [
    config.headers,
    config.headers.common,
    config.headers[method],
    config.headers.get,
    config.headers.post,
    config.headers.put,
    config.headers.delete,
    config.headers.patch,
  ].filter(Boolean)

  const want = headerName.toLowerCase()
  candidates.forEach((h) => {
    Object.keys(h).forEach((key) => {
      if (key.toLowerCase() === want) delete h[key]
    })
  })
}

// ==================== Token 刷新（已迁移到 refreshToken.js）====================
// 保留 refreshJavaToken 作为向后兼容的别名
const refreshJavaToken = () => {
  return refreshTokenByType('java')
}

// Add a request interceptor
/* eslint-disable no-undef */
axios.interceptors.request.use(
  config => {
    // console.log('[Request Interceptor] 请求发出:', config.method, config.url)

    // 统一提取 axiosType 到 config.axiosType（用于响应拦截器）
    // POST/PUT 请求：从 data 中提取
    if (config.data && typeof config.data === 'object' && config.data.axiosType) {
      const axiosType = config.data.axiosType
      // 如果 axiosType 是已定义的格式，保存到 config.axiosType
      if (API_FORMAT_CONFIG[axiosType]) {
        config.axiosType = axiosType
        // 从 data 中删除 axiosType，避免它被发送到服务器
        delete config.data.axiosType
      }
    }
    
    // GET 请求：从 params 中提取
    if (config.params && typeof config.params === 'object' && config.params.axiosType) {
      const axiosType = config.params.axiosType
      // 如果 axiosType 是已定义的格式，保存到 config.axiosType
      if (API_FORMAT_CONFIG[axiosType]) {
        config.axiosType = axiosType
        // 从 params 中删除 axiosType，避免它被作为查询参数发送
        delete config.params.axiosType
      }
    }

    // 存在 killPending 属性时，执行http请求取消操作
    if (config.params && config.params.killPending) {
      removePending(config)
      config.cancelToken = new cancelToken((c) => {
        // 这里的axios标识我是用请求地址&请求方式拼接的字符串，当然你可以选择其他的一些方式
        pending.push({ u: config.url + '&' + config.method, f: c })
      })
      delete config.params.killPending
    }

    // 添加请求头显示目标服务器 IP 信息（所有环境）
    const url = config.url || ''
    const baseURL = config.baseURL || axios.defaults.baseURL || ''
    
    // 计算最终请求 URL
    let finalUrl = url
    if (baseURL && !url.startsWith('http://') && !url.startsWith('https://')) {
      if (!url.startsWith('/')) {
        finalUrl = `${baseURL}/${url}`.replace(/\/+/g, '/')
      } else {
        finalUrl = url
      }
    }

    // 跨域请求：移除可能触发 preflight 且后端未放行的 header，避免 CORS 报错
    // 典型报错：Request header field cache-control is not allowed by Access-Control-Allow-Headers
    if (isCrossOriginRequest(finalUrl)) {
      deleteHeader(config, 'Cache-Control')
      deleteHeader(config, 'Pragma')
    } else if ((config.method || '').toLowerCase() === 'get') {
      // 同源 GET：如确实需要禁止缓存，可以在这里加（不会造成跨域 preflight 问题）
      // config.headers['Cache-Control'] = 'no-cache'
      // config.headers['Pragma'] = 'no-cache'
    }
    
    // 确定目标服务器 IP/域名
    let targetServer = 'N/A'
    let targetIP = 'N/A'
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // 直接请求完整 URL
      const urlObj = new URL(url)
      targetServer = `${urlObj.protocol}//${urlObj.host}`
      targetIP = urlObj.hostname
    } else if (url.startsWith('/api.php')) {
      // 统一从 BasicConfig 获取，根据环境变量自动切换
      // 开发环境：从 .env.development 读取（测试服务器）
      // 测试环境：从 .env.test 读取（测试服务器）
      // 生产环境：从 .env.production 读取（正式服务器）
      targetServer = BasicConfig.shixinlDomain || BasicConfig.domain || 'N/A'
      try {
        const urlObj = new URL(targetServer)
        targetIP = urlObj.hostname
      } catch (e) {
        targetIP = targetServer.replace(/^https?:\/\//, '').split(':')[0]
      }
    } else if (url.startsWith('/tapi')) {
      // 统一从 BasicConfig 获取，根据环境变量自动切换
      // /tapi 需要添加端口 5000
      const baseDomain = BasicConfig.shixinlDomain || BasicConfig.domain || 'N/A'
      targetServer = baseDomain.includes(':') ? baseDomain : `${baseDomain}:5000`
      try {
        const urlObj = new URL(targetServer)
        targetIP = urlObj.hostname
      } catch (e) {
        targetIP = targetServer.replace(/^https?:\/\//, '').split(':')[0]
      }
    } else if (url.startsWith('/api') || url.startsWith('api')) {
      // 统一从 BasicConfig 获取，根据环境变量自动切换
      targetServer = BasicConfig.domain || 'N/A'
      try {
        const urlObj = new URL(targetServer)
        targetIP = urlObj.hostname
      } catch (e) {
        targetIP = targetServer.replace(/^https?:\/\//, '').split(':')[0]
      }
    } else if (baseURL) {
      // 使用 baseURL 作为目标
      targetServer = baseURL
      try {
        const urlObj = new URL(baseURL)
        targetIP = urlObj.hostname
      } catch (e) {
        targetIP = baseURL.replace(/^https?:\/\//, '').split(':')[0]
      }
    } else if (BasicConfig.szMysqlDomain) {
      // 使用 szMysqlDomain 作为目标
      targetServer = BasicConfig.szMysqlDomain
      try {
        const urlObj = new URL(BasicConfig.szMysqlDomain)
        targetIP = urlObj.hostname
      } catch (e) {
        targetIP = BasicConfig.szMysqlDomain.replace(/^https?:\/\//, '').split(':')[0]
      }
    }
    
    // 添加请求头
    // config.headers['X-Target-Server'] = targetServer
    // config.headers['X-Target-IP'] = targetIP
    // config.headers['X-Request-URL'] = finalUrl
    
    // 开发环境额外调试信息
    if (process.env.NODE_ENV === 'development') {
      console.log('[API Debug]', {
        baseURL,
        url,
        finalUrl,
        targetServer,
        targetIP,
        szMysqlDomain: BasicConfig.szMysqlDomain,
        domain: BasicConfig.domain,
      })
    }

      // 获取 API 格式配置
      const format = getApiFormat(config)
    
    // 检查 token（如果没有 token，不发送请求）
    if (!checkTokenBeforeRequest(config, format)) {
      // 非 java/ys7 类型：无 token 时跳转登录页
      const axiosType = config?.axiosType || 'default'
      const shouldRedirectToLogin = !isJavaAxiosFamily(axiosType) && axiosType !== 'ys7'
      if (shouldRedirectToLogin) {
        Session.removeIkCookie()
        window.location.replace('/system/login')
      }
      return Promise.reject(new Error('请求被拒绝：缺少认证信息'))
    }
    
    // 获取 API 类型（ys7、java、erpAPI、erpIndex、default）
    const apiType = Object.keys(API_FORMAT_CONFIG).find(key => API_FORMAT_CONFIG[key] === format) || 'default'
    
    // 根据 API 类型执行对应的 token 处理
    switch (apiType) {
      case 'ys7':
        handleYs7Token(config, format)
        break
      case 'java':
        handleJavaToken(config, format)
        break
      case 'erpAPI':
      case 'erpIndex':
        handleErpToken(config, format)
        break
      default:
        handleDefaultToken(config, format)
    }

    // 数据采用json方式提交
    // if (config.method === 'post' || config.method === 'put') {
    //   config.headers['Content-Type'] = 'application/json;charset=UTF-8'
    // }

    // 数据采用formData方式提交
    // convert object body to FormData
    // if (config.data && config.method === 'post') {
    //   config.data = objToFormData(config.data)
    // }
    // // convert object body to URLSearchParams
    // if (config.data && config.method === 'put') {
    //   config.data = objToQuerystring(config.data)
    // }
    
    // 配置 数据提交方式，更新 请求头
    const bodyFormatConfig = {
      // FormData
      'post':  (config) => {
        //  form-data 请求头
        // config.headers['Content-Type'] = 'multipart/form-data;'
        config.data = objToFormData(config.data)
      },
      // Json
      'put': (config) => {
        // json 请求头
        config.headers['Content-Type'] = 'application/json;charset=UTF-8'
        config.data = objToQuerystring(config.data)
      },
      'putFormData': (config) => {
        config.headers['Content-Type'] = 'multipart/form-data'
        config.data = objToFormData(config.data)
      },
      'ctJson':(config) => {
        config.headers['Content-Type'] = 'application/json;charset=UTF-8'
      },
      'erpAPI':(config) => {
        // 统一设置 Content-Type 和数据格式转换
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8'
        config.headers['x-requested-with'] = 'XMLHttpRequest'
        config.data = objToFormData(config.data)
      },
      'erpIndex':(config) => {
        // 统一设置 Content-Type 和数据格式转换
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8'
        config.headers['x-requested-with'] = 'XMLHttpRequest'
        config.withCredentials = true // 携带 cookie
        config.data = objToFormData(config.data)
      },
      'erpAPIEncrypted':(config) => {
        // 为需要加密的 ERP API 接口准备数据
        // 用于 MemberController 等继承 CommonController 的接口
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8'
        config.headers['x-requested-with'] = 'XMLHttpRequest'
        // 使用加密函数处理数据
        const encryptedData = objToEncryptedData(config.data)
        // 将加密后的数据转换为 form-urlencoded 格式
        config.data = objToQuerystring(encryptedData)
      },
      // Java API 配置（仅用于 POST/PUT 请求）
      'java': (config) => {
        // 只处理有 body 的请求（POST/PUT），GET 请求不处理
        if (config.data && typeof config.data === 'object') {
          // 统一设置 Content-Type 和数据格式转换
          config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
          config.data = objToQuerystring(config.data)
        }
      },
      /** Java API，请求体 JSON（不经过 objToQuerystring） */
      'javaJson': (config) => {
        if (config.data && typeof config.data === 'object') {
          config.headers['Content-Type'] = 'application/json;charset=UTF-8'
        }
      },
      /** Java API，multipart：原生 FormData，Content-Type 为 multipart/form-data */
      'javaMultipart': (config) => {
        if (!config.data) return
        const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData
        if (!isFormData && typeof config.data === 'object') {
          config.data = objToFormData(config.data)
        }
        deleteHeader(config, 'Content-Type')
        config.headers = config.headers || {}
        config.headers['Content-Type'] = 'multipart/form-data'
      },
      // 萤石云 API 配置（仅用于 POST/PUT 请求）
      'ys7': (config) => {
        // 只处理有 body 的请求（POST/PUT），GET 请求不处理
        if (config.data && typeof config.data === 'object') {
          // 统一设置 Content-Type 和数据格式转换
          config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
          config.data = objToQuerystring(config.data)
        }
      }
    }
    
    // 提交方式 默认 config.method 请求方式
    // 只处理有 body 的请求（POST/PUT），GET 请求不处理
    let bodyType = config.method
    if (config.data) {
      /** 
       * 如果 需要指定 数据类型，可通过 axiosType 改变
       * axiosType: put -> json
       * axiosType: post -> FormData
       * axiosType: ys7 -> 萤石云 API 格式
       * axiosType: java -> Java API 格式（form-urlencoded）
       * axiosType: javaJson -> Java API，body 为 JSON
       * axiosType: javaMultipart -> Java API，body 为 FormData
       * axiosType: erpAPI/erpIndex -> ERP API 格式
       * 为了 非要 post用 put请求方法
       * */ 
      bodyType = config.axiosType || bodyType
      
      if (bodyFormatConfig[bodyType]) {
        bodyFormatConfig[bodyType](config)
      } else if (bodyType === 'post' || bodyType === 'POST') {
        // 默认 POST 请求：如果没有指定格式，使用 form-urlencoded
        if (config.data && typeof config.data === 'object') {
          config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
          config.data = objToQuerystring(config.data)
        }
      }
    }

    return config
  },
  error => {
    // Do something with request error
    return Promise.reject(error)
  }
)

// Add a response interceptor
/* eslint-disable no-undef */
axios.interceptors.response.use(
  response => {
    // console.log('[Response Interceptor] 成功响应，进入 formatResponse', response)
    return formatResponse(response)
  },
  async error => {
    // Do something with response error
    // console.log('[Response Interceptor] 错误响应，未进入 formatResponse', error)
    // console.log('[Response Interceptor] error.response:', error.response)
    // console.log('[Response Interceptor] error.response?.data:', error.response?.data)
    
    // 获取 API 格式配置
    const format = getApiFormat(error.config)
    // 根据配置获取状态码
    const statusCode = error.response?.data?.[format.statusCodeField]
    
    // 从 config.axiosType 获取（请求拦截器已自动提取）
    const axiosType = error.config?.axiosType || 'default'
    
    // 统一处理登录失效：根据 axiosType 自动刷新 token 并重试
    if (
      statusCode !== undefined &&
      isLoginExpired(statusCode, format, error.response?.data) &&
      error?.config &&
      !error.config.__skipAuthRefresh &&
      !error.config.__retriedAfterRefresh
    ) {
      try {
        // 使用统一的 token 刷新方法
        await handleLoginExpired(axiosType, statusCode)

        // 标记已重试，防止死循环
        const retryConfig = { ...error.config, __retriedAfterRefresh: true }

        // 重试前：清理可能导致跨域 preflight 的 header（按现有规则）
        try {
          const url = retryConfig.url || ''
          const baseURL = retryConfig.baseURL || axios.defaults.baseURL || ''
          let finalUrl = url
          if (baseURL && !url.startsWith('http://') && !url.startsWith('https://')) {
            if (!url.startsWith('/')) {
              finalUrl = `${baseURL}/${url}`.replace(/\/+/g, '/')
            } else {
              finalUrl = url
            }
          }
          if (isCrossOriginRequest(finalUrl)) {
            deleteHeader(retryConfig, 'Cache-Control')
            deleteHeader(retryConfig, 'Pragma')
          }
        } catch (e) {
          // ignore
        }

        return axios(retryConfig)
      } catch (e) {
        // 刷新失败或不支持自动刷新：继续走原来的登录失效处理（跳登录）
        console.warn(`[api.js] ${axiosType} token 刷新失败或不支持自动刷新:`, e)
      }
    }

    // 如果错误响应中有 data 且状态码表示需要登录，则处理（支持多个登录失效码）
    if (statusCode !== undefined && !error?.config?.__skipAuthRefresh && isLoginExpired(statusCode, format, error.response?.data)) {
      // console.log('[Response Interceptor] 检测到需要登录，执行跳转')
      // 清除 token 和 cookies
      // - 只有“session token 体系”（默认/ERP）才需要清理 ik cookie
      // - Java token 存在 Redux/localStorage，不写入 cookies，不能用 cookies 来校验/清理
      // 从 config.axiosType 获取（请求拦截器已自动提取）
      const axiosType = error.config?.axiosType || 'default'
      
      // 检查是否支持自动刷新
      const shouldTryRefresh = isJavaAxiosFamily(axiosType) || axiosType === 'ys7'
      
      if (!shouldTryRefresh) {
        // 不支持自动刷新的类型（如 ERP），直接跳转登录
        const isSessionTokenSource = !format?.tokenSource || format.tokenSource === 'session'
        if (isSessionTokenSource) {
          Session.removeIkCookie()
        }
        // 使用 window.location 强制跳转到登录页
        window.location.replace('/system/login')
      }
      // 支持自动刷新的类型（java/ys7），如果刷新失败，也会走到这里，但不会跳转（由调用方决定）
    }
    
    return Promise.reject(error)
  }
)

export default axios
